/**
 * CommandPalette refinement.
 *
 * Changes from original:
 *   - ElevatedSurface (tier4) wraps the dialog body
 *   - useDeferredValue on search query for snappy typing
 *   - Recent searches stored in localStorage (key: leviathan.command-recent, max 10)
 *   - Results grouped into Pages, Tickers, Actions
 *   - Full nav item list searchable as "Pages" group
 *   - Esc closes, arrows navigate, Enter activates (cmdk built-in)
 *
 * Do not add em-dashes anywhere.
 */

import { useEffect, useCallback, useState, useDeferredValue } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Activity,
  BarChart2,
  BarChart3,
  Bell,
  BrainCircuit,
  FileInput,
  FlaskConical,
  GitCompare,
  Home,
  Layers,
  LayoutDashboard,
  PieChart,
  Scale,
  Search,
  Send,
  ShieldCheck,
  Sigma,
  Sliders,
  Sparkles,
  TrendingUp,
  Users,
  Wrench,
  Zap,
  Banknote,
  Clock,
} from 'lucide-react'
import {
  CommandDialog,
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
} from '@/components/ui/command'
import { useAppStore } from '@/stores/app'
import { elevation, type as typeTokens, color } from '@/design-system/tokens'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const RECENT_KEY = 'leviathan.command-recent'
const RECENT_MAX = 10

interface NavPage {
  label: string
  to: string
  icon: React.ComponentType<{ className?: string }>
}

const ALL_PAGES: NavPage[] = [
  { label: 'Dashboard',       to: '/dashboard',       icon: LayoutDashboard },
  { label: 'Screen',          to: '/screen',          icon: BarChart3 },
  { label: 'Compare',         to: '/compare',         icon: GitCompare },
  { label: 'Manual Input',    to: '/manual-input',    icon: FileInput },
  { label: 'M&A Analyzer',    to: '/mna',             icon: Scale },
  { label: 'Apophis',         to: '/apophis',         icon: Send },
  { label: 'Apophis Documents', to: '/apophis/documents', icon: Send },
  { label: 'Watchlist',       to: '/watchlist',       icon: Bell },
  { label: 'Events',          to: '/events',          icon: Zap },
  { label: 'Portfolio',       to: '/portfolio',       icon: PieChart },
  { label: 'Estimates',       to: '/estimates',       icon: TrendingUp },
  { label: 'Track Record',    to: '/backtesting',     icon: BarChart2 },
  { label: 'Factor Analytics',to: '/factors',         icon: Sigma },
  { label: 'ML Insights',     to: '/ml-insights',     icon: BrainCircuit },
  { label: 'Strategies',      to: '/strategies',      icon: FlaskConical },
  { label: 'Moat Analysis',   to: '/moat',            icon: Sparkles },
  { label: 'Credit Universe', to: '/credit-universe', icon: Banknote },
  { label: 'Crowded Trades',  to: '/crowded-trades',  icon: Users },
  { label: 'Batch Analysis',  to: '/batch',           icon: Layers },
  { label: 'Compliance',      to: '/compliance',      icon: ShieldCheck },
  { label: 'Analytics',       to: '/analytics',       icon: Activity },
  { label: 'Dev Tools',       to: '/devtools',        icon: Wrench },
  { label: 'Settings',        to: '/settings',        icon: Sliders },
  { label: 'Profile',         to: '/profile',         icon: Home },
]

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    // Defensive: tolerate user-tampered storage. Filter to string entries.
    return parsed.filter((x): x is string => typeof x === 'string')
  } catch {
    return []
  }
}

function writeRecent(items: string[]): void {
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, RECENT_MAX)))
  } catch {
    // localStorage may be unavailable (e.g., SSR or private browsing)
  }
}

function addToRecent(label: string): void {
  const existing = readRecent().filter((l) => l !== label)
  writeRecent([label, ...existing])
}

// ---------------------------------------------------------------------------
// CommandPalette
// ---------------------------------------------------------------------------

export default function CommandPalette() {
  const open = useAppStore((s) => s.commandPaletteOpen)
  const setOpen = useAppStore((s) => s.setCommandPaletteOpen)
  const navigate = useNavigate()

  const [query, setQuery] = useState('')
  const deferredQuery = useDeferredValue(query)
  const [recentLabels, setRecentLabels] = useState<string[]>(() => readRecent())

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(!open)
      }
    },
    [open, setOpen],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  const runCommand = useCallback(
    (label: string, path: string) => {
      addToRecent(label)
      setRecentLabels(readRecent())
      setOpen(false)
      setQuery('')
      navigate(path)
    },
    [setOpen, navigate],
  )

  // Filter pages by deferred query
  const lowerQuery = deferredQuery.toLowerCase()
  const filteredPages = lowerQuery
    ? ALL_PAGES.filter((p) => p.label.toLowerCase().includes(lowerQuery))
    : ALL_PAGES

  // Recent items (resolved to page objects)
  const recentPages = recentLabels
    .map((label) => ALL_PAGES.find((p) => p.label === label))
    .filter((p): p is NavPage => !!p)

  // Tickers group. Shows:
  //   1) A live "Analyze {QUERY}" item when the query shape matches a ticker
  //      (1 to 5 uppercase letters, no other chars).
  //   2) Up to the last 5 ticker symbols this session has analyzed, read from
  //      the same recent store but keyed with a `ticker:` prefix so pages and
  //      tickers do not collide.
  const tickerQueryMatch = /^[a-zA-Z]{1,5}$/.test(query.trim())
    ? query.trim().toUpperCase()
    : null
  const recentTickers = recentLabels
    .filter((label) => label.startsWith('ticker:'))
    .map((label) => label.slice('ticker:'.length))
    .slice(0, 5)
  const tickerGroupItems: Array<{ symbol: string; hint: string }> = []
  if (tickerQueryMatch) {
    tickerGroupItems.push({ symbol: tickerQueryMatch, hint: 'Open analysis' })
  }
  for (const sym of recentTickers) {
    if (sym !== tickerQueryMatch) {
      tickerGroupItems.push({ symbol: sym, hint: 'Recent' })
    }
  }

  // Dialog overlay + content use tier4 glass
  const tier4 = elevation.tier4

  return (
    <CommandDialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v)
        if (!v) setQuery('')
      }}
    >
      <div
        style={{
          ...tier4,
          borderRadius: '12px',
          overflow: 'hidden',
        }}
      >
        <Command
          shouldFilter={false}
          style={{
            background: 'transparent',
          }}
        >
          <CommandInput
            placeholder="Search pages, tickers, actions..."
            value={query}
            onValueChange={setQuery}
          />
          <CommandList>
            <CommandEmpty>
              <span style={{ fontSize: `${typeTokens.bodySmall.size}px`, color: color.graySecondary }}>
                No results found.
              </span>
            </CommandEmpty>

            {/* Recent searches (only when query is empty) */}
            {!deferredQuery && recentPages.length > 0 && (
              <>
                <CommandGroup heading="Recent">
                  {recentPages.slice(0, 5).map((page) => (
                    <CommandItem
                      key={page.to}
                      value={page.label}
                      onSelect={() => runCommand(page.label, page.to)}
                    >
                      <Clock className="size-4 text-gray-secondary" />
                      <span>{page.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* Pages */}
            {filteredPages.length > 0 && (
              <CommandGroup heading="Pages">
                {filteredPages.map((page) => (
                  <CommandItem
                    key={page.to}
                    value={page.label}
                    onSelect={() => runCommand(page.label, page.to)}
                  >
                    <page.icon className="size-4" />
                    <span>{page.label}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}

            {/* Tickers: recent tickers + live "Analyze <QUERY>" when the query
                matches a ticker shape (1-5 uppercase letters). */}
            {tickerGroupItems.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Tickers">
                  {tickerGroupItems.map((t) => (
                    <CommandItem
                      key={`ticker-${t.symbol}`}
                      value={`ticker ${t.symbol}`}
                      onSelect={() => runCommand(`ticker:${t.symbol}`, `/analysis/${t.symbol}`)}
                    >
                      <TrendingUp className="size-4" style={{ color: color.gold }} />
                      <span style={{ fontFamily: 'var(--font-mono)' }}>{t.symbol}</span>
                      <span style={{ marginLeft: 'auto', fontSize: '10px', color: color.graySecondary }}>
                        {t.hint}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}

            {/* Actions group */}
            {(!lowerQuery || 'analyze'.includes(lowerQuery) || 'screen'.includes(lowerQuery)) && (
              <>
                <CommandSeparator />
                <CommandGroup heading="Actions">
                  {(!lowerQuery || 'analyze ticker'.includes(lowerQuery)) && (
                    <CommandItem
                      value="Analyze Ticker"
                      onSelect={() => runCommand('Dashboard', '/dashboard')}
                    >
                      <TrendingUp className="size-4" style={{ color: color.gold }} />
                      <span>Analyze Ticker</span>
                      <span style={{ marginLeft: 'auto', fontSize: '10px', color: color.graySecondary }}>
                        Enter ticker on Dashboard
                      </span>
                    </CommandItem>
                  )}
                  {(!lowerQuery || 'screen stocks'.includes(lowerQuery)) && (
                    <CommandItem
                      value="Screen Stocks"
                      onSelect={() => runCommand('Screen', '/screen')}
                    >
                      <Search className="size-4" style={{ color: color.purpleBright }} />
                      <span>Screen Stocks</span>
                    </CommandItem>
                  )}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </div>
    </CommandDialog>
  )
}
